<template>
    <v-app style="overflow-x: hidden" :class="{ blur: showCredits, mobile: smAndDown }" :theme="store.theme" fluid>
        <v-main width="100%" max-width="1920" class="mx-auto">
            <router-view v-slot="{ Component }">
                <keep-alive>
                    <component :is="Component" @update:theme="store.theme = store.theme === 'light' ? 'dark' : 'light'" @error="errorHandler" @signup="signup = true" />
                </keep-alive>
            </router-view>
        </v-main>
        <v-snackbar text :timeout="-1" v-model="snackbar.active" style="opacity: 0.95" @click="snackbarCloseHandler" :color="store.theme === 'dark' ? 'grey-lighten-4' : 'grey-darken-4'">
            <v-row>
                <v-col cols="1" class="d-flex align-center justify-center py-0">
                    <v-icon :icon="snackbar.icon" :color="snackbar.iconColor" :size="smAndDown ? 'x-small' : 'x-large'" class="ml-2" />
                </v-col>
                <v-col cols="10" class="d-flex align-center justify-center py-0">
                    <span v-if="snackbar.message" v-html="snackbar.message"></span>
                    <span v-else @click="reload" class="font-weight-light" v-bind:class="smAndDown ? 'caption' : ''" style="cursor: pointer">App update available.</span>
                </v-col>
                <v-col cols="1" class="d-flex align-center justify-center py-0">
                    <v-btn variant="plain" :size="smAndDown ? 'x-small' : 'default'" @click="snackbarCloseHandler" icon="cancel" />
                </v-col>
            </v-row>
        </v-snackbar>
    </v-app>
</template>
<style>
.kg-image {
    max-width: -webkit-fill-available;
    object-fit: contain;
}

.image-wrapper::before,
.image-wrapper::after {
    content: "";
    position: absolute;
    left: 0;
    width: 100%;
    height: 100px;
    pointer-events: none;
    z-index: 1;
    /* On top of the images */
}

.image-wrapper::before {
    top: 0;
    height: 30px;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
}

.image-wrapper::after {
    bottom: 0;
    height: 20px;
    background: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
}
</style>
<script setup>
import { ref, provide, inject } from 'vue'
import { useDadStore } from './store/dad'
import { useDisplay } from 'vuetify/lib/framework.mjs'
import 'animate.css'
import posthog from 'posthog-js'
import { useHead } from '@unhead/vue'
import { useTranslation } from "i18next-vue"
import { until } from 'async'

posthog.init('phc_5uN0U5vX3fTkEj8vODVRBSMytawIdmjR3Rr92rz2pOh',
    {
        api_host: 'https://us.i.posthog.com',
        person_profiles: 'always'
    }
)

const { t } = useTranslation()
const clipboard = inject('clipboard')
const store = useDadStore()
const { PROD } = import.meta.env
const resetLocalStorage = ref(false)
const signup = ref(false)
const { smAndDown } = useDisplay()
const emit = defineEmits(['update:theme', 'error', 'signup'])
const error = ref(false)
const version = ref()
const snackbarDefault = {
    active: false,
    icon: 'info',
    message: undefined,
}
const snackbar = ref({ ...snackbarDefault })
const lastBuild = ref()
const showCredits = ref(false)
const versionCheckIntervalId = ref()
const buildInfo = ref()

useHead({
  title: t('head.title'),
  meta: [
    { name: 'title', content: t('head.title') },
    { name: 'description', content: t('head.metaDescription') },
    { name: 'keywords', content: t('head.metaKeywords') },
    { property: 'og:title', content: t('head.title') },
    { property: 'og:description', content: t('head.metaDescription') },
    { name: 'og:twitter:title', content: t('head.title') },
    { name: 'og:twitter:description', content: t('head.metaDescription') },
  ],
})
const checkVersion = async () => {
    if (!PROD && !/dev\./.test(window.location.host)) {
        return
    }
    try {
        const response = await fetch(`${window.origin}/build-info.json`, {
            headers: {
                'Cache-Control': 'no-store, max-age=0',
            },
        })

        const data = await response.json()

        buildInfo.value = data
    } catch (error) {
        console.error(error)
    }

    if (!buildInfo.value?.build_date) {
        return
    }
    version.value = buildInfo.value.commit_sha
    if (lastBuild.value && lastBuild.value?.build_date !== buildInfo.value.build_date) {
        snackbar.value.active = true
        // TODO: put some extra check here...
        resetLocalStorage.value = true
    } else {
        lastBuild.value = buildInfo.value
    }
}
function errorHandler(errorMessage) {
    error.value = true
    snackbar.value = {
        active: true,
        icon: 'error',
        iconColor: 'red',
        message: errorMessage,
    }
}
function snackbarCloseHandler() {
    snackbar.value.active = false
    setTimeout(() => {
        snackbar.value = { ...snackbarDefault }
        if (error.value) {
            error.value = false
        }
    }, 500)
}
function reload() {
    const url = new URL(window.location.href)
    url.searchParams.set('cache', Date.now())
    window.location.href = url.toString()
}
checkVersion()

versionCheckIntervalId.value = setInterval(checkVersion, 60000)

function cleanPayload(data) {
    const payload = Object.fromEntries(Object.entries({
        ...data,
    }).filter(([_key, value]) => value))

    return payload
}
async function copyHandler(tooltips, tooltipName, value) {
    try {
        const copied = await clipboard.copy(value)
        if (copied && !(copied instanceof Error)) {
            tooltips[tooltipName] = true
        }
    } finally {
        setTimeout(() => {
            tooltips[tooltipName] = false
        }, 1500)
    }
}
function scrollSectionIntoView(sectionElement) {
    setTimeout(() => {
        sectionElement?.scrollIntoView(true)
    }, 500)
}
function withErrorHandling(func, finallyFunc, errorFunc) {
    try {
        func()
    } catch (error) {
        if (errorFu) errorFunc()
        else console.warn(error)
    } finally {
        if (finallyFunc) finallyFunc()
    }
}
async function waitForX(x, func) {
    await until(
        callback => callback(null, x.value),
        async () => await new Promise(resolve => setTimeout(resolve)),
    )
    func()
}
async function getMessengerId(sio) {
    if (!sio) return store.messengerId
    return await new Promise(resolve => sio.emit('getMessengerId', json => {
        const { id, error } = json

        if (error) {
            throw error
        }

        store.messengerId = json.messengerId

        resolve(store.messengerId)
    }))
}
provide('getMessengerId', getMessengerId)
provide('waitForX', waitForX)
provide('withErrorHandling', withErrorHandling)
provide('scrollSectionIntoView', scrollSectionIntoView)
provide('copyHandler', copyHandler)
provide('cleanPayload', cleanPayload)
</script>
